import React, { useState } from 'react';
import { Navigate } from "react-router-dom";

import { ArrowBack } from '@mui/icons-material';
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  FeatureBase, 
  ModelBase, 
} from "@models";
import { 
  FeatureBaseWithRules,
  StandardFeatures, 
} from "@dndModels";
import { DATA_TYPE_INFO, CHAR_FEATURE_TYPES } from "@dndModels/Constants";
import { FeatureType } from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import FeatureBaseInput from "@dndComponents/feature/FeatureBaseInput";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import FeatureBasesInput from "@dndComponents/feature/FeatureBasesInput";
import SelectStringBox from "@components/input/selects/SelectStringBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function StandardFeaturesEdit({ obj: param_obj }: Props) {
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<StandardFeatures>(param_obj instanceof StandardFeatures ? param_obj : api.applyWorkingOn(new StandardFeatures()));
  const [change_time, setChangeTime] = useState(0);
  const [tab, setTab] = useState("Description");
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBaseWithRules | null>(null);
  const [level_filter, setLevelFilter] = useState(-1);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  const renderTab = () => {
    let content: any = null;
    const height = window.innerHeight - 64;
    if (tab === "Description") {
      content = [
        <Grid
          key="description"
          size={12}>
          <ModelBaseDetails
            obj={obj}
            disabled={disabled}
            change_time={change_time}
            // image_options={image_options}
            onChange={(changed: ModelBase) => {
              if (changed instanceof StandardFeatures) {
                const new_obj = obj;
                new_obj.copy(changed);
                setObj(new_obj);
              }
            }}
          />
        </Grid>
      ];
    } else if (tab === "Features") {
      // console.log(obj.feature_bases);
      content = (
        <Grid
          size={{
            xs: 12
          }}>
          <FeatureBasesInput 
            feature_bases={obj.feature_bases} 
            feature_source={obj}
            // parent_id={obj._id} 
            // parent_type="Race"
            disabled={disabled}
            height={height}
            onExpand={(changed: FeatureBase) => {
              if (changed instanceof FeatureBaseWithRules) {
                setExpandedFeatureBase(changed);
              }
            }}
            level_filter={level_filter}
            onLevelFilterChange={setLevelFilter}
            onAdd={() => {
              const new_obj = obj;
              const feature_base = new FeatureBaseWithRules();
              feature_base.level = level_filter > -1 ? level_filter : 1;
              feature_base.feature_source_id = new_obj._id;
              feature_base.feature_source_type = new_obj.feature_source_type;
              new_obj.feature_bases.push(feature_base);
              setObj(new_obj);
              setExpandedFeatureBase(feature_base);
              setChangeTime(common_util.get_time_number());
            }}
            onReorder={(updated) => {
              const new_obj = obj;
              for (const u of updated) {
                const fb = new_obj.feature_bases.find(o => o.true_id === u.true_id);
                if (fb) {
                  fb.copy(u);
                }
              }
              // new_obj.feature_bases = updated;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      );
    }
    return (
      <Grid
        style={{ height }}
        size={{
          xs: 12
        }}>
        <div style={{ height: "100%" }}>
          <Grid container spacing={0} direction="row" style={{ 
            maxHeight: "100%", 
            overflowY: "auto", 
            width: "100%", 
            overflowX: "hidden",
          }}>
            { content }
          </Grid>
        </div>
      </Grid>
    );
  }

  const changeTab = (newTab: string) => {
    setTab(newTab);
  }

  const renderControls = () => {
    if (expanded_feature_base) {
      if (expanded_feature_base instanceof FeatureBaseWithRules) {
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.feature_bases}
              feature_source={obj}
              disabled={disabled}
              change_time={change_time}
              allowed_feature_types={[expanded_feature_base.feature_type]}
              onChange={(changed: FeatureBase) => {
                // debugger;
                const new_obj = obj;
                const objFinder = new_obj.feature_bases.find(o => o.id === changed.id);
                if (objFinder) {
                  const feature_base = objFinder;
                  feature_base.copy(changed);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  const feature_bases = new_obj.feature_bases.filter(o => o.id !== id);
                  new_obj.feature_bases = feature_bases;
                  setObj(new_obj);
                  setExpandedFeatureBase(null);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            >
              <Grid container spacing={0} direction="row">
                <Grid
                  className="header"
                  size={{
                    xs: 12
                  }}>
                  Rules
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name="Feature Type"
                    options={CHAR_FEATURE_TYPES.sort((a,b) => { return a.localeCompare(b); })}
                    value={expanded_feature_base.feature_type}
                    disabled={disabled}
                    onChange={(changed: FeatureType) => {
                      const new_obj = obj;
                      const objFinder = new_obj.feature_bases.find(o => o.id === expanded_feature_base.id);
                      if (objFinder) {
                        const feature_base = objFinder;
                        feature_base.feature_type = changed;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name="Look In"
                    options={[{ id: "race", text: "Race" }, { id: "background", text: "Background" }]}
                    values={expanded_feature_base.look_in}
                    disabled={disabled}
                    multiple
                    onChange={(changed: ("race" | "background")[]) => {
                      const new_obj = obj;
                      const objFinder = new_obj.feature_bases.find(o => o.id === expanded_feature_base.id);
                      if (objFinder) {
                        const feature_base = objFinder;
                        feature_base.look_in = changed;
                        if (changed.length === 2 && feature_base.dominant_feature_source === "") {
                          feature_base.dominant_feature_source = "race";
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  { expanded_feature_base.look_in.length > 1 &&
                    <SelectStringBox
                      name="Dominant Feature Source"
                      options={[{ id: "race", text: "Race" }, { id: "background", text: "Background" }]}
                      value={expanded_feature_base.dominant_feature_source}
                      disabled={disabled}
                      onChange={(changed: "race" | "background") => {
                        const new_obj = obj;
                        const objFinder = new_obj.feature_bases.find(o => o.id === expanded_feature_base.id);
                        if (objFinder) {
                          const feature_base = objFinder;
                          feature_base.dominant_feature_source = changed;
                          setObj(new_obj);
                          setChangeTime(common_util.get_time_number());
                        }
                      }}
                    />
                  } 
                </Grid>
              </Grid>
            </FeatureBaseInput>
          </div>
        );
      }
    } else { 
      const dt_info = DATA_TYPE_INFO.find(o => o.data_type === obj.data_type);
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <Grid container spacing={0} direction="row" style={{ height: "100%" }}>
            <Grid
              size={{
                xs: 3
              }}>
              <Tooltip title={`Back to ${dt_info?.plural??""}`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  onClick={ () => {
                    setNavigateTo(`/${obj.data_type}`);
                  }}>
                  <ArrowBack/>
                </Fab>
              </Tooltip>
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <span className="header">
                { obj._id === "" ? `Create ${dt_info?.name??"Object"}` : `Edit ${obj.name}` }
              </span>
            </Grid>
            <Grid
              style={{ height: "80px" }}
              size={{
                xs: 12
              }}>
              <StringBox 
                value={obj.name} 
                message={obj.name.length > 0 ? "" : "Name Invalid"}
                name="Name"
                disabled={disabled}
                onBlur={(value: string) => {
                  const new_obj = obj;
                  new_obj.name = value;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              style={{ height: "23px" }}
              size={{
                xs: 12
              }}>
              <CenteredMenu 
                options={["Description","Features"]}
                selected={tab}
                onSelect={changeTab}
              />
            </Grid>
            { renderTab() }
          </Grid>
        </div>
      ); 
    }
  }

  const height = window.innerHeight - 64;
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else { 
    return (
      <div style={{ height }}>
        <Grid container spacing={0} direction="row" style={{ height: "100%" }}>
          <Grid
            container
            spacing={0}
            direction="row"
            style={{ height: (tab === "Description" ? (height - 61) : height) }}
            size={{
              xs: 12
            }}>
            <Grid
              style={{ height: "100%" }}
              size={{
                xs: 12
              }}>
              { renderControls() }
            </Grid>
          </Grid>
        </Grid>
      </div>
    ); 
  }
}